import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { color, font } from '../styles/theme';
import { fullDate } from '../lib/format';
import { makeLookup, groupsOf, identityOf } from '../lib/presenters';
import {
  advanceStatus,
  sideStatuses,
  groupDot,
  studyKeyOf,
  studySlugOf,
  ENDORSED,
} from '../lib/pipeline';
import { StaffForms } from './StaffForms';
import { Conversation, NOT_A_MATCH, NoLetterNote } from './Conversation';
import { useMeta, useSubmission, useSetStatus, usePeople } from '../api/queries';
import { useSetOwner } from '../api/conversation';
import { useEndorse } from '../api/endorsements';
import { useStaffRecords } from '../api/staff';
import { useAuth } from '../auth/AuthContext';
import { EndorseControl } from '../components/EndorseControl';
import { PeopleOptions } from '../components/ContactBits';
import {
  Modal,
  Button,
  Confirm,
  Field,
  SectionHead,
  Loading,
  ErrorNote,
  RedactedBadge,
  selectStyle,
} from '../components/ui';

/**
 * One participant, opened from the board.
 *
 * Laid out as a chart rather than a settings page: a banner that keeps the
 * identifiers on screen while you scroll, alerts directly under it, the record
 * in ruled sections, and the status controls pinned to the bottom so nobody has
 * to scroll past the whole history to act.
 *
 * The status ladder is the API's (07 §2), so this offers the next funnel step
 * as the primary action and every off-funnel state — waitlist, hold, not a
 * match — as an explicit choice. Nothing here shows an internal impression: 02
 * is clear that scores, "high risk" and "fail" language never reach a view a
 * participant could be shown.
 *
 * Beside them, Endorse to… hands the person to another study's team. It is
 * not a status choice: the row changes study, lands in that board's Newly
 * endorsed column, and stays on this one's Endorsed out.
 *
 * "Or move to → Not a match" says, in its confirmation, that no letter goes
 * out on its own: the move opens a clock, and the letter is sent from the
 * conversation with the "Not a match" template.
 */

/**
 * Nothing was entered here.
 *
 * The presenters fill an empty field with an em dash, which on a chart reads as
 * a rendering fault rather than an answer — someone checking whether a phone
 * number is missing or merely hidden cannot tell from a dash. This says it, and
 * says it in the muted weight so it is never mistaken for the participant's own
 * words.
 */
const BLANK = 'No input';

function isBlank(v: string): boolean {
  return v === '—' || v.trim() === '';
}

export function ParticipantDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const meta = useMeta();
  const detail = useSubmission(id);
  const setStatus = useSetStatus();
  const setOwner = useSetOwner();
  const endorse = useEndorse();
  const qc = useQueryClient();
  const { can } = useAuth();
  const canUpdate = can('submissions:update');
  // Staff records are PHI: a role without read_phi is refused them, so it
  // never asks, and the staff panel says why it is empty.
  const canSeeStaff = can('submissions:read_phi');
  // Already fetched by the staff panel below; this is the same cached query, and
  // it is read here so an open flag can be shown as a banner at the top of the
  // record instead of halfway down it.
  const staff = useStaffRecords(canSeeStaff ? id : null);

  // A move is confirmed before it is sent: the status decides the sentence the
  // participant is shown about their own application.
  const [confirming, setConfirming] = useState<'next' | 'side' | 'endorse' | null>(null);
  const [side, setSide] = useState('');
  const [endorseTo, setEndorseTo] = useState('');

  // A rejected status change (an open safety flag, say) leaves its message on
  // screen. Once the record refetches — because the blocker was cleared — the
  // note is stale and misleading, so drop it.
  const freshAt = detail.dataUpdatedAt;
  const resetStatus = setStatus.reset;
  useEffect(() => {
    resetStatus();
  }, [freshAt, resetStatus]);
  // Anyone who may change the record may give it an owner; the people come
  // from `/admins/people`, which every signed-in role can read.
  const people = usePeople(canUpdate);

  const r = detail.data?.submission;
  const current = meta.data && r ? meta.data.statuses.find((s) => s.key === r.status) : undefined;
  const openFlags = staff.data?.counts.openFlags ?? 0;

  function move(status: string) {
    setConfirming(null);
    setSide('');
    setStatus.mutate(
      { id, status },
      {
        onSuccess: () => {
          // The move opened a clock; the clock lists should show it now.
          if (status === NOT_A_MATCH) void qc.invalidateQueries({ queryKey: ['tickets'] });
        },
      },
    );
  }

  // The person leaves the board this was opened from — and, for someone who
  // does not work on the other study, their reach — so the record closes.
  function handOver(study: string) {
    setConfirming(null);
    setEndorseTo('');
    endorse.mutate({ id, study }, { onSuccess: onClose });
  }

  const header = (
    <div style={{ paddingRight: 4 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <h2
          style={{
            fontFamily: font.display,
            fontWeight: 600,
            fontSize: 23,
            lineHeight: '30px',
            color: color.navy,
            margin: 0,
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {r?.name ?? 'Participant'}
          {r?.redacted && <RedactedBadge />}
        </h2>
        {current && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              fontWeight: 800,
              padding: '6px 13px',
              borderRadius: 999,
              background: color.tint,
              color: color.blueDeep,
              flex: 'none',
              marginTop: 2,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: groupDot[current.group],
              }}
            />
            {current.label}
          </span>
        )}
      </div>
      {r && meta.data && <Identifiers r={r} meta={meta.data} />}
    </div>
  );

  function body() {
    if (detail.isLoading || meta.isLoading) return <Loading />;
    if (detail.error) return <ErrorNote message={(detail.error as Error).message} />;
    if (!detail.data || !meta.data || !r) return null;

    const { meta: fm, history } = detail.data;
    const L = makeLookup(meta.data);
    // The banner above already carries these, so the sections drop them rather
    // than saying everything twice.
    const shown = new Set(identityOf(r, L).map((i) => i.k));
    const groups = groupsOf(r, L)
      .map((g) => ({ ...g, fields: g.fields.filter((x) => !shown.has(x.k)) }))
      .filter((g) => g.fields.length || g.long.length);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        {openFlags > 0 && (
          <p
            style={{
              background: '#fdf1ee',
              border: '1px solid #f3d5cc',
              borderLeft: `3px solid ${color.red}`,
              color: color.red,
              borderRadius: 12,
              padding: '11px 14px',
              fontSize: 12.5,
              fontWeight: 700,
              margin: 0,
            }}
          >
            {openFlags} open safety {openFlags === 1 ? 'flag' : 'flags'} — this person cannot be
            marked enrolled until they are closed.
          </p>
        )}

        {r.endorsedFrom && (
          <p
            style={{
              background: color.tint,
              borderLeft: `3px solid ${color.blue}`,
              color: color.blueDeep,
              borderRadius: 12,
              padding: '11px 14px',
              fontSize: 12.5,
              lineHeight: '18px',
              margin: 0,
            }}
          >
            <strong style={{ fontWeight: 800 }} title={r.endorsedFrom.label}>
              Endorsed from {r.endorsedFrom.short}
            </strong>{' '}
            on {fullDate(r.endorsedFrom.at)}. Their form still names the study they asked for.
          </p>
        )}

        {current?.blurb && (
          <p
            style={{
              background: color.tint,
              borderLeft: `3px solid ${color.blue}`,
              color: color.blueDeep,
              borderRadius: 12,
              padding: '11px 14px',
              fontSize: 12.5,
              lineHeight: '18px',
              margin: 0,
            }}
          >
            <strong style={{ fontWeight: 800 }}>What they are shown</strong> — “{current.blurb}”
          </p>
        )}

        {groups.map((g) => (
          <section key={g.title}>
            <SectionHead>{g.title}</SectionHead>
            {/*
              Label above value, three across.

              The old rows put the label left and the value right, which reads
              as a settings list and — once the panel was widened — stranded
              each label a long way from its own value. Stacking the pair keeps
              them together at any width and lets a long study title wrap
              instead of ragging off to the right.
            */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                columnGap: 26,
                // A section with one or two fields would otherwise lay a rule
                // and three empty columns across the full width, which reads as
                // missing content rather than a short section.
                maxWidth: g.fields.length < 4 ? g.fields.length * 236 : undefined,
              }}
            >
              {g.fields.map((f) => (
                <div
                  key={f.k}
                  style={{ padding: '10px 0', borderTop: `1px solid ${color.line}`, minWidth: 0 }}
                >
                  <Label>{f.k}</Label>
                  <p
                    style={{
                      fontSize: 13.5,
                      fontWeight: isBlank(f.v) ? 500 : 600,
                      lineHeight: '19px',
                      color: isBlank(f.v) ? color.muted : color.ink,
                      margin: '4px 0 0',
                    }}
                  >
                    {isBlank(f.v) ? BLANK : f.v}
                  </p>
                </div>
              ))}
            </div>
            {g.long.map((f) => (
              <div
                key={f.k}
                style={{
                  background: color.bg,
                  border: `1px solid ${color.line}`,
                  borderLeft: `3px solid ${color.tint2}`,
                  borderRadius: 12,
                  padding: '12px 14px',
                  marginTop: 14,
                }}
              >
                <Label>{f.k}</Label>
                <p
                  style={{
                    fontSize: 13.5,
                    lineHeight: '20px',
                    color: isBlank(f.v) ? color.muted : color.ink,
                    margin: '5px 0 0',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {isBlank(f.v) ? BLANK : f.v}
                </p>
              </div>
            ))}
          </section>
        ))}

        <section>
          <StaffForms
            submissionId={r.id}
            form={r.form}
            studySlug={studyKeyOf(r)}
            canRead={canSeeStaff}
            canUpdate={canUpdate}
            hideFlagBanner
          />
        </section>

        <section>
          <Conversation submission={r} fm={fm} history={history} meta={meta.data} />
        </section>

        <p style={{ fontSize: 11.5, color: color.muted, margin: 0 }}>
          {fm.label} · added {fullDate(r.createdAt)} · routes to {fm.route} · {fm.sla}
        </p>
      </div>
    );
  }

  function footer() {
    if (!meta.data || !r) return null;
    const next = advanceStatus(meta.data, r);
    const accepting = r.status === ENDORSED;
    const sides = sideStatuses(meta.data, r);
    const sideStatus = sides.find((s) => s.key === side);

    const here = studySlugOf(r);
    const hereShort = meta.data.studies.find((s) => s.key === here)?.short ?? 'this study';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {setStatus.error && <ErrorNote message={(setStatus.error as Error).message} />}
        {endorse.error && <ErrorNote message={(endorse.error as Error).message} />}
        {setOwner.error && <ErrorNote message={(setOwner.error as Error).message} />}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          {canUpdate ? (
            <>
              {next && (
                <Confirm
                  open={confirming === 'next'}
                  title={
                    accepting
                      ? `Accept ${r.name} into ${hereShort}?`
                      : `Move ${r.name} to ${next.label}?`
                  }
                  detail={
                    next.blurb
                      ? accepting
                        ? `They move to ${next.label}, and will be shown: “${next.blurb}”`
                        : `They will be shown: “${next.blurb}”`
                      : undefined
                  }
                  confirmLabel={accepting ? 'Accept' : 'Move'}
                  onConfirm={() => move(next.key)}
                  onCancel={() => setConfirming(null)}
                >
                  <Button
                    onClick={() => setConfirming('next')}
                    disabled={setStatus.isPending}
                    title="You will be asked to confirm"
                  >
                    {accepting ? `Accept → ${next.label}` : `Move to ${next.label}`}
                  </Button>
                </Confirm>
              )}
              {/* Off-funnel states are chosen, never stumbled into. */}
              <Confirm
                open={confirming === 'side' && !!sideStatus}
                title={sideStatus ? `Move ${r.name} to ${sideStatus.label}?` : ''}
                detail={
                  sideStatus?.key === NOT_A_MATCH ? (
                    <>
                      {sideStatus.blurb && (
                        <span style={{ display: 'block', marginBottom: 6 }}>
                          They will be shown: “{sideStatus.blurb}”
                        </span>
                      )}
                      <NoLetterNote />
                    </>
                  ) : sideStatus?.blurb ? (
                    `They will be shown: “${sideStatus.blurb}”`
                  ) : undefined
                }
                confirmLabel="Move"
                onConfirm={() => sideStatus && move(sideStatus.key)}
                onCancel={() => {
                  setConfirming(null);
                  setSide('');
                }}
              >
                <select
                  value={side}
                  onChange={(e) => {
                    setSide(e.target.value);
                    setConfirming(e.target.value ? 'side' : null);
                  }}
                  disabled={setStatus.isPending}
                  aria-label="Move to another status"
                  style={{ ...selectStyle, width: 210, fontSize: 13 }}
                >
                  <option value="">Or move to…</option>
                  {sides.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </Confirm>
              {/* The same control as on the inbox rows and the board cards. */}
              <EndorseControl
                row={r}
                meta={meta.data}
                size="field"
                target={confirming === 'endorse' ? endorseTo || null : null}
                onTarget={(s) => {
                  setEndorseTo(s ?? '');
                  setConfirming(s ? 'endorse' : null);
                }}
                onEndorse={handOver}
                pending={endorse.isPending}
              />
            </>
          ) : (
            <span style={{ fontSize: 12.5, color: color.muted }}>
              Your role cannot change a participant's status.
            </span>
          )}
          <span style={{ flex: 1 }} />
          {/* The reply itself now lives in the conversation above; what belongs
              beside the status controls is who is carrying this record. */}
          <Field label="Owner" style={{ width: 190 }}>
            {canUpdate ? (
              <select
                value={r.ownerId ?? ''}
                onChange={(e) => setOwner.mutate({ id, body: { ownerId: e.target.value || null } })}
                disabled={setOwner.isPending || people.isLoading}
                style={{ ...selectStyle, fontSize: 13 }}
              >
                <option value="">Unassigned</option>
                <PeopleOptions people={people.data?.people} current={{ id: r.ownerId, name: r.ownerName }} />
              </select>
            ) : (
              <span style={{ fontSize: 13, fontWeight: 600, color: r.ownerName ? color.ink : color.muted }}>
                {r.ownerName ?? 'Unassigned'}
              </span>
            )}
          </Field>
        </div>
      </div>
    );
  }

  return (
    <Modal
      header={header}
      footer={r ? footer() : undefined}
      onClose={onClose}
      width={1040}
      label="Participant detail"
    >
      {body()}
    </Modal>
  );
}

/**
 * The identifier strip under the name.
 *
 * Kept in the sticky header on purpose: these are the fields someone checks to
 * be sure the record on screen belongs to the person on the phone, and that
 * check is worth as little as the scroll position it depends on. Items the API
 * withheld are dropped rather than shown as dashes, so the strip does not turn
 * into a row of blanks on a redacted record.
 */
function Identifiers({
  r,
  meta,
}: {
  r: NonNullable<ReturnType<typeof useSubmission>['data']>['submission'];
  meta: NonNullable<ReturnType<typeof useMeta>['data']>;
}) {
  const items = identityOf(r, makeLookup(meta)).filter((i) => i.v && i.v !== '—');
  if (!items.length) return null;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 20px', margin: '9px 0 0' }}>
      {items.map((i) => (
        <span key={i.k} style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
          <Label>{i.k}</Label>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>{i.v}</span>
        </span>
      ))}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 10.5,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: color.muted,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}
